import { PlatformSDKShape } from '../PlatformSDKShape';
import { BaseSDKShape } from '../BaseSDKShape';
import {
  type IFontValue,
  type FontPickerValue,
  parseFontString,
  UNDERLINE_DEFINITION,
} from './utils';

interface IColorValue {
  color: string;
  theme?: string;
  cssVariableName?: string;
}

type IColorPickerValue = string | null | undefined;

function fontValueToCSS(fontValue: IFontValue): string {
  return `${
    fontValue.cssVariableName ? `var(${fontValue.cssVariableName}, ` : ''
  }${fontValue.italic ? 'italic ' : ''}${fontValue.bold ? 'bold ' : ''}${fontValue.size || 16}px ${
    // wrap each font family with quotes
    fontValue.family
      ?.split(',')
      .map((fontFamily) => `"${fontFamily.replace(/['"]+/g, '')}"`)
      .join(',') || 'serif'
  }${fontValue.cssVariableName ? ')' : ''}`;
}

function parseColorString(
  colorPickerValue?: IColorPickerValue,
): IColorValue | null {
  if (!colorPickerValue) {
    return null;
  }
  const colorString = colorPickerValue!;
  const match = colorString.match(/var\((--[\w-]+),\s*(.+)\)/);

  if (match) {
    return {
      cssVariableName: match[1],
      color: match[2]!,
    };
  } else {
    return {
      color: colorString,
    };
  }
}

function colorValueToCSS(colorValue?: IColorValue | null): string {
  if (!colorValue) {
    return '';
  }
  return colorValue.cssVariableName
    ? `var(${colorValue.cssVariableName}, ${colorValue.color})`
    : colorValue.color;
}

type IFontValueInternal = {
  preset?: string;
  editorKey?: string;
  family?: string;
  size?: number;
  style: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
  };
  htmlTag?: string;
};

const fonts = {
  transformFontInternalValue: (value?: IFontValue): IFontValueInternal => {
    if (value) {
      const { theme, cssVariableName, bold, italic, underline, ...rest } =
        value;

      return {
        ...rest,
        style: {
          bold,
          italic,
          underline,
        },
        editorKey: cssVariableName || theme || '',
      };
    } else {
      return {
        editorKey: 'font_7',
        family: 'helvetica-w01-roman',
        size: 16,
        style: {},
      };
    }
  },
};

export class InputsSDKShape extends BaseSDKShape {
  async selectColor(
    value?: IColorPickerValue,
    options?: {
      onChange?: (value: IColorPickerValue) => void;
    },
  ) {
    const privateAPI = await this.getPlatformPrivateAPI();

    let colorValue: IColorValue | null = parseColorString(value);
    let colorResult = colorValueToCSS(colorValue);

    await privateAPI.inputs.openColorPicker(
      {
        color:
          colorValue?.cssVariableName || colorValue?.theme || colorValue?.color,
      },
      ({
        color,
        theme,
        cssVariableTheme,
      }: {
        color: string;
        theme: string;
        cssVariableTheme: string;
        isHover: boolean;
      }) => {
        colorValue = {
          color,
          theme,
          cssVariableName: cssVariableTheme,
        };
        colorResult = colorValueToCSS(colorValue);
        options?.onChange?.(colorResult);
      },
    );

    return colorResult;
  }

  async selectFont(
    value?: FontPickerValue,
    options?: {
      onChange?: (value: FontPickerValue) => void;
    },
  ) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const fontValue = parseFontString(value);
    let _value = value;

    await privateAPI.inputs.openFontPickerV2(
      {
        ...options,
        panelSectionsDefinition: {
          htmlTag: 'hidden',
        },
        componentStyle: fonts.transformFontInternalValue(fontValue),
      },
      (font: IFontValue, accessibility: any) => {
        _value = {
          font: fontValueToCSS(font),
          textDecoration: font.underline ? UNDERLINE_DEFINITION : undefined,
        };
        options?.onChange?.(_value);
      },
    );

    return _value;
  }
}

export default new PlatformSDKShape(InputsSDKShape)
  .withPublicMethod('selectColor')
  .withPublicMethod('selectFont')
  .build();
